import { useMutation, useQueryClient } from '@tanstack/react-query';

import { uploadWorkspaceFile } from '@/lib/api/generated/api';
import { expectStatus } from '@/lib/api/client';
import { FOLDER_PLACEHOLDER_NAME } from '../folders';
import { fileKeys } from '../queryKeys';

/**
 * Creates an empty folder inside `currentPath`.
 *
 * The workspace's storage has no directories — a folder is a key prefix, and
 * a prefix exists only while something is stored under it. So an empty folder
 * is made by writing the one file that holds its prefix open, and that file is
 * hidden from every listing (`isFolderPlaceholder`).
 *
 * There is no create-directory operation in the contract to call instead: the
 * files API is list, read, upload, delete, register and history.
 */
export function useCreateWorkspaceFolder(
  workspaceId: string,
  currentPath: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string): Promise<string> => {
      const folderPath = currentPath ? `${currentPath}/${name}` : name;
      expectStatus(
        await uploadWorkspaceFile(workspaceId, {
          path: `${folderPath}/${FOLDER_PLACEHOLDER_NAME}`,
          file: new File([], FOLDER_PLACEHOLDER_NAME, { type: 'text/plain' }),
        }),
        200,
        `“${name}” could not be created. Please try again.`,
      );
      return folderPath;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: fileKeys.listing(workspaceId, currentPath),
      }),
  });
}
